/**
 * "Are you sure?" as a window of the app's own (2026-10-23, the owner: deleting an event needs
 * "a notification popup to make sure"), instead of the browser's plain confirm box.
 *
 * askConfirm() opens it and resolves true or false; one ConfirmHost, at the top of the app
 * (main.tsx), draws it. Escape or a click outside is "no".
 */
import { useEffect, useRef, useState } from 'react';
import { t } from './strings.ts';

export interface ConfirmAsk {
  title: string;
  body?: string;
  /** The button that goes ahead: "Delete", say. */
  yes: string;
  /** A red button, for what cannot be undone. */
  danger?: boolean;
}
type Pending = ConfirmAsk & { resolve: (yes: boolean) => void };
let openWindow: ((ask: Pending) => void) | null = null;

/** Ask, and wait for the answer. Without a host (it should not happen), the browser's own box. */
export function askConfirm(ask: ConfirmAsk): Promise<boolean> {
  if (!openWindow) return Promise.resolve(confirm([ask.title, ask.body].filter(Boolean).join('\n\n')));
  return new Promise((resolve) => openWindow!({ ...ask, resolve }));
}

export function ConfirmHost() {
  const [ask, setAsk] = useState<Pending | null>(null);
  const yesBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    openWindow = setAsk;
    return () => { openWindow = null; };
  }, []);
  const answer = (yes: boolean) => {
    ask?.resolve(yes);
    setAsk(null);
  };
  useEffect(() => {
    if (!ask) return;
    yesBtn.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') answer(false); };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  });
  if (!ask) return null;
  return (
    <div className="confirm-veil no-print" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) answer(false); }}>
      <div className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby={ask.body ? 'confirm-body' : undefined}>
        <h2 id="confirm-title">{ask.title}</h2>
        {ask.body && <p id="confirm-body">{ask.body}</p>}
        <div className="confirm-buttons">
          <button type="button" onClick={() => answer(false)}>{t('cancel')}</button>
          <button type="button" ref={yesBtn} className={ask.danger ? 'danger' : 'primary'} onClick={() => answer(true)}>{ask.yes}</button>
        </div>
      </div>
    </div>
  );
}
